import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

export interface DropItem {
  id: number
  title: string
  videoUrl: string
  thumbnailUrl?: string
  tags?: string
  status: 'draft' | 'published'
  order: number
  liked: boolean
  watched: boolean
  _count: { drop_likes: number; drop_views: number }
}

export function useDrops() {
  return useQuery({
    queryKey: ['drops'],
    queryFn: async () => {
      const { data } = await api.get<DropItem[]>('/drops')
      return data
    },
  })
}

export function useRecordDropView() {
  return useMutation({
    mutationFn: async (dropId: number) => {
      await api.post(`/drops/${dropId}/view`)
    },
  })
}

export function useToggleDropLike() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dropId: number) => {
      const { data } = await api.post<{ liked: boolean }>(`/drops/${dropId}/like`)
      return data
    },
    onMutate: async (dropId: number) => {
      await queryClient.cancelQueries({ queryKey: ['drops'] })
      const previous = queryClient.getQueryData<DropItem[]>(['drops'])
      queryClient.setQueryData<DropItem[]>(['drops'], (old) =>
        old?.map((drop) =>
          drop.id === dropId
            ? {
                ...drop,
                liked: !drop.liked,
                _count: {
                  ...drop._count,
                  drop_likes: drop.liked ? drop._count.drop_likes - 1 : drop._count.drop_likes + 1,
                },
              }
            : drop
        )
      )
      return { previous }
    },
    onError: (_err, _dropId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['drops'], context.previous)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['drops'] })
    },
  })
}
